import { CommonModule } from '@angular/common';
import {
  Component,
  Input,
  TemplateRef,
  forwardRef,
  ViewChild,
  ElementRef,
  OnDestroy,
  ChangeDetectorRef,
  EventEmitter,
  Output,
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { NbButtonModule, NbDialogService, NbSpinnerModule } from '@nebular/theme';
import { NebularModule } from '../../modules/nebular/nebular.module';
import { extractReadableFileName, formatFileSize } from '../../utils/string';

interface FileItem {
  file?: File;
  fileType?: string;
  fileUrl?: string;
  fileName: string;
  fileSize?: string;
  objectUrl?: string;
  originalFileSize?: string;
  sizeReductionPercent?: number;
}

type FileKind = 'image' | 'video' | 'pdf' | 'audio' | 'file';

@Component({
  standalone: true,
  selector: 'ngx-file-uploader',
  host: {
    '[attr.data-pwa-busy]': 'processing',
    '[attr.data-pwa-draft]': 'hasPendingFiles',
  },
  imports: [CommonModule, NbButtonModule, NbSpinnerModule, NebularModule],
  templateUrl: './file-uploader.component.html',
  styleUrls: ['./file-uploader.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => FileUploaderComponent),
      multi: true,
    },
  ],
})
export class FileUploaderComponent implements ControlValueAccessor, OnDestroy {
  @Input() label?: string;
  @Input() multiple?: boolean = false;
  @Input() accept?: string;
  @Input() compact = false;
  @Input() fileProcessor?: (file: File) => Promise<File>;
  @Input() processingMessage = 'در حال آماده‌سازی فایل...';
  @Output() processingChange = new EventEmitter<boolean>();

  @ViewChild('fileInput', { static: false })
  fileInput!: ElementRef<HTMLInputElement>;

  fileItems: FileItem[] = [];
  disabled = false;
  invalidFileMessage = '';
  processing = false;

  get hasPendingFiles(): boolean {
    return this.fileItems.some(item => !!item.file);
  }
  private selectionVersion = 0;
  private destroyed = false;

  /** CVA callbacks */
  private onChange: (value: (File | string)[] | null) => void = () => {};
  private onTouched: () => void = () => {};

  constructor(
    private dialogService: NbDialogService,
    private readonly cdr: ChangeDetectorRef,
  ) {}

  // -----------------------------
  // File selection
  // -----------------------------
  async onFileSelected(event: Event): Promise<void> {
    if (this.disabled || this.processing) return;

    const input = event.target as HTMLInputElement;
    if (!input.files || input.files.length === 0) return;

    const files = Array.from(input.files);
    const acceptedFiles = files.filter((file) => this.isAcceptedFile(file));
    this.invalidFileMessage = acceptedFiles.length === files.length
      ? ''
      : this.accept?.trim() === 'image/*'
        ? 'لطفاً یک تصویر انتخاب کنید.'
        : this.accept?.includes('application/pdf')
          ? 'فقط فایل PDF قابل انتخاب است.'
          : 'لطفاً یک فایل صوتی انتخاب کنید.';
    if (!acceptedFiles.length) {
      input.value = '';
      this.onTouched();
      return;
    }

    const selectedFiles = this.multiple ? acceptedFiles : acceptedFiles.slice(0, 1);
    const version = ++this.selectionVersion;
    this.onTouched();
    input.value = '';

    try {
      let preparedFiles = selectedFiles;
      if (this.fileProcessor) {
        this.setProcessing(true);
        preparedFiles = await Promise.all(selectedFiles.map((file) => this.fileProcessor!(file)));
      }

      if (this.destroyed || this.disabled || version !== this.selectionVersion) return;

      const newItems = preparedFiles.map((file, index) => this.createFileItem(file, selectedFiles[index]));
      if (!this.multiple) {
        this.fileItems.forEach((item) => this.revokePreview(item));
      }
      this.fileItems = this.multiple ? [...this.fileItems, ...newItems] : newItems;
      this.emitValue();
    } catch (error) {
      if (!this.destroyed && version === this.selectionVersion) {
        this.invalidFileMessage = error instanceof Error
          ? error.message
          : 'آماده‌سازی فایل انجام نشد؛ لطفاً دوباره تلاش کنید.';
      }
    } finally {
      if (!this.destroyed && version === this.selectionVersion) {
        this.setProcessing(false);
        this.cdr.markForCheck();
      }
    }
  }

  // -----------------------------
  // Emit CVA value
  // -----------------------------
  private emitValue(): void {
    if (!this.fileItems.length) {
      this.onChange(null);
      return;
    }

    const files = this.fileItems.map((item) => item.file || item.fileUrl);
    // .filter((f): f is File => !!f);

    this.onChange(files);
    // ✅ single-file mode emits File
    // if (!this.multiple) {
    //   this.onChange(files[0] ?? null);
    // } else {
    //   this.onChange(files);
    // }
  }

  // -----------------------------
  // ControlValueAccessor
  // -----------------------------
  writeValue(
    value: File | File[] | FileItem[] | string | string[] | null
  ): void {
    this.selectionVersion++;
    this.setProcessing(false);
    this.fileItems.forEach((item) => this.revokePreview(item));
    this.fileItems = [];
    this.invalidFileMessage = '';

    if (!value) return;

    const allValues = Array.isArray(value) ? value : [value];
    const values = this.multiple ? allValues : allValues.slice(0, 1);

    values.forEach((v) => {
      if (v instanceof File) {
        this.fileItems.push(this.createFileItem(v));
      } else if (typeof v === 'string') {
        this.fileItems.push({
          fileUrl: v,
          fileName: extractReadableFileName(v),
        });
      } else if (v?.fileUrl) {
        this.fileItems.push({
          fileType: v?.fileType,
          fileUrl: v?.fileUrl,
          fileName: v?.fileName || extractReadableFileName(v?.fileUrl),
          fileSize: v?.fileSize,
        });
      }
    });

    // 🔒 SINGLE FILE MODE → keep only first
    if (!this.multiple && this.fileItems.length > 1) {
      this.fileItems = [this.fileItems[0]];
    }
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  // -----------------------------
  // UI actions
  // -----------------------------
  handleSeeFile(dialog: TemplateRef<any>, item: FileItem): boolean {
    if (!item.fileUrl || !['image', 'video'].includes(this.getFileKind(item))) return false;

    this.dialogService.open(dialog, {
      context: { fileUrl: item.fileUrl, fileType: item.fileType },
    });

    this.onTouched();
    return false;
  }

  handleDeleteFile(index: number): boolean {
    if (this.disabled || this.processing) return false;

    const [removed] = this.fileItems.splice(index, 1);
    this.revokePreview(removed);
    this.emitValue();
    this.onTouched();

    return false;
  }

  openPdf(item: FileItem): void {
    if (item.fileUrl && this.getFileKind(item) === 'pdf') {
      window.open(item.fileUrl, '_blank', 'noopener,noreferrer');
      this.onTouched();
    }
  }

  ngOnDestroy(): void {
    this.destroyed = true;
    this.selectionVersion++;
    this.fileItems.forEach((item) => this.revokePreview(item));
  }

  private setProcessing(processing: boolean): void {
    if (this.processing === processing) return;
    this.processing = processing;
    this.processingChange.emit(processing);
  }

  private createFileItem(file: File, originalFile: File = file): FileItem {
    const objectUrl = URL.createObjectURL(file);
    const reduced = file.size < originalFile.size;
    return {
      file,
      fileType: file.type || (this.accept?.includes('application/pdf') ? 'application/pdf' : ''),
      fileUrl: objectUrl,
      objectUrl,
      fileName: file.name,
      fileSize: formatFileSize(file.size),
      ...(reduced && {
        originalFileSize: formatFileSize(originalFile.size),
        sizeReductionPercent: Math.round((1 - file.size / originalFile.size) * 100),
      }),
    };
  }

  private revokePreview(item?: FileItem): void {
    if (item?.objectUrl) URL.revokeObjectURL(item.objectUrl);
  }

  getFileKind(item: FileItem): FileKind {
    const type = item.fileType || '';
    const name = item.fileName?.toLowerCase() || '';
    if (type.startsWith('image/')) return 'image';
    if (type.startsWith('video/')) return 'video';
    if (type === 'application/pdf') return 'pdf';
    if (type.startsWith('audio/')) return 'audio';
    if (/\.(png|jpe?g|webp|gif|svg)$/.test(name)) return 'image';
    if (/\.(mp3|m4a|wav|ogg|oga|aac|opus)$/.test(name)) return 'audio';
    if (/\.(mp4|webm|mov)$/.test(name)) return 'video';
    if (name.endsWith('.pdf')) return 'pdf';
    return 'file';
  }

  private isAcceptedFile(file: File): boolean {
    if (this.accept?.trim() === 'image/*') {
      return file.type.startsWith('image/') ||
        (!file.type && /\.(png|jpe?g|webp|gif|svg|bmp|avif|heic|heif|tiff?)$/i.test(file.name));
    }
    if (this.accept?.includes('application/pdf')) {
      return file.type === 'application/pdf' || file.name.toLowerCase().endsWith('.pdf');
    }
    if (this.accept?.trim() === 'audio/*') {
      return file.type.startsWith('audio/') || /\.(mp3|m4a|wav|ogg|oga|aac|webm|opus)$/.test(file.name.toLowerCase());
    }
    return true;
  }
}
